import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { ToolbarModule } from 'primeng/toolbar';
import { InputNumberModule } from 'primeng/inputnumber';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Settlement } from '../settlement.model';
import { SettlementService } from '../settlement.service';
import { forkJoin, of } from 'rxjs';

import { AppUser } from '../../users/user.model';
import { UserService } from '../../users/users.service';
import { PromoPlanHeader } from '../../promo-plan/promo-plan.model';
import { PromoPlanService } from '../../promo-plan/promo-plan.service';
import { Customer, CustomerPickerSummary } from '../../master-data/customers/customers.model';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Branch } from '../../master-data/branches/branch.model';
import { BranchesService } from '../../master-data/branches/branches.service';
import { BusinessUnit } from '../../master-data/business-units/business-unit.model';
import { BusinessUnitsService } from '../../master-data/business-units/business-units.service';
import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';
import { ActivityType } from '../../master-data/activity-type/activity-type.model';
import { ActivityTypeService } from '../../master-data/activity-type/activity-type.service';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { ChipModule } from 'primeng/chip';
import { IftaLabelModule } from "primeng/iftalabel";
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Page } from '../../models/page-wrapper.model';
import { EntityDropdownPickerComponent } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { EntityDropdownMultiselectPickerComponent } from '../../shared/components/entity-dropdown-multiselect-picker/entity-dropdown-multiselect-picker.component';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { formatNormalizedDate } from '../../shared/utils/date-normalization.util';


@Component({
  selector: 'app-settlement-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    CalendarModule,
    TagModule,
    ToastModule,
    ToolbarModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    ChipModule,
    DropdownModule,
    IftaLabelModule,
    AppDatePipe,
    EntityDropdownPickerComponent,
    EntityDropdownMultiselectPickerComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './list-settlement.component.html',
  styleUrls: ['./list-settlement.component.css'],
})
export class ListSettlementComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  settlements: Settlement[] = [];
  totalRecords = 0;
  loading: boolean = false;
  isExporting: boolean = false;
  page: number = 0;
  rows: number = 25;

  filterForm: FormGroup;

  statuses: string[] = [
    'Pending - Budgetary Control',
    'Pending - Sales',
    'Pending - Marketing',
    'Pending - A&P Controller',
    'Pending - Finance AR',
    'Cancelled',
    'Completed'
  ];

  // Options for filter dropdowns
  customerPickerAdapter!: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;
  salesRepPickerAdapter!: EntityDropdownPickerAdapter<AppUser>;
  branchPickerAdapter!: EntityDropdownPickerAdapter<Branch>;
  businessUnitPickerAdapter!: EntityDropdownPickerAdapter<BusinessUnit>;
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;
  activityTypePickerAdapter!: EntityDropdownPickerAdapter<ActivityType>;
  supplierPickerAdapter!: EntityDropdownPickerAdapter<Supplier>;
  promoPlanPickerAdapter!: EntityDropdownPickerAdapter<PromoPlanHeader>;

  startDateOptions = [
    'equal to', 'not equal to', 'greater than or equal to', 'less than or equal to', 'is between'
  ];
  endDateOptions = [
    'equal to', 'not equal to', 'greater than or equal to', 'less than or equal to', 'is between'
  ];

  constructor(
    private router: Router,
    private settlementService: SettlementService,
    private messageService: MessageService,
    private userService: UserService,
    private promoPlanService: PromoPlanService,
    private customerService: CustomersService,
    private branchService: BranchesService,
    private businessUnitService: BusinessUnitsService,
    private businessService: BusinessService,
    private activityTypeService: ActivityTypeService,
    private supplierService: SuppliersService,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      id: [null],
      salesRepNames: [[]],
      statuses: [[]],
      promoPlanIds: [null],
      actualStartDate: [null],
      actualEndDate: [null],
      customers: [null],
      channels: [[]],
      branchNames: [[]],
      businessUnitNames: [[]],
      businessNames: [[]],
      activityTypeNames: [[]],
      supplierNames: [[]],
      note: [null],
      value: [null],
      vat: [null],
      totalAmount: [null],
      customerInvoiceNumbers: [[]],
      actualStartDateOption: ['equal to'],
      actualEndDateOption: ['equal to']
    });
  }

  ngOnInit() {
    this.customerPickerAdapter = this.customerService.createCustomerPickerAdapter();
    this.salesRepPickerAdapter = this.userService.createUserPickerAdapter();
    this.branchPickerAdapter = this.branchService.createBranchPickerAdapter();
    this.businessUnitPickerAdapter = this.businessUnitService.createBusinessUnitPickerAdapter();
    this.businessPickerAdapter = this.businessService.createBusinessPickerAdapter();
    this.activityTypePickerAdapter = this.activityTypeService.createActivityTypePickerAdapter();
    this.supplierPickerAdapter = this.supplierService.createSupplierPickerAdapter();
    this.promoPlanPickerAdapter = this.createPromoPlanPickerAdapter();
  }

  loadSettlements(event: TableLazyLoadEvent = {}) {
    this.loading = true;
    const filters = this.filterForm.value;

    const searchParams: any = {
      page: event.first ? (event.first / (event.rows || this.rows)) : this.page,
      size: event.rows || this.rows,
      settlementId: filters.id,
      salesRepNames: filters.salesRepNames?.map((r: AppUser) => r.fullName),
      statuses: filters.statuses,
      promoPlanIds: filters.promoPlanIds?.promoPlanId ? [filters.promoPlanIds.promoPlanId] : undefined,
      customerNumbers: filters.customers?.customerNumber ? [filters.customers.customerNumber] : undefined,
      channels: filters.channels,
      branchNames: filters.branchNames?.map((b: Branch) => b.name),
      businessUnitNames: filters.businessUnitNames?.map((bu: BusinessUnit) => bu.businessUnitName),
      businessNames: filters.businessNames?.map((b: Business) => b.businessName),
      activityTypeNames: filters.activityTypeNames?.map((at: ActivityType) => at.name),
      supplierNames: filters.supplierNames?.map((s: Supplier) => s.supplierName),
      note: filters.note,
      value: filters.value,
      vat: filters.vat,
      totalAmount: filters.totalAmount,
      customerInvoiceNumbers: filters.customerInvoiceNumbers
    };

    if (event.sortField) {
      searchParams.sort = `${event.sortField},${event.sortOrder === 1 ? 'asc' : 'desc'}`;
    } else {
      searchParams.sort = 'id,desc';
    }

    switch (filters.actualStartDateOption) {
      case 'equal to':
        if (filters.actualStartDate) {
          searchParams.actualStartDateFrom = this.formatDate(filters.actualStartDate);
          searchParams.actualStartDateTo = this.formatDate(filters.actualStartDate);
        }
        break;
      case 'not equal to':
        if (filters.actualStartDate) {
          searchParams.actualStartDateFrom = this.formatDate(new Date(new Date(filters.actualStartDate).setDate(new Date(filters.actualStartDate).getDate() + 1)));
          searchParams.actualStartDateTo = this.formatDate(new Date(new Date(filters.actualStartDate).setDate(new Date(filters.actualStartDate).getDate() - 1)));
        }
        break;
      case 'greater than or equal to':
        if (filters.actualStartDate) searchParams.actualStartDateFrom = this.formatDate(filters.actualStartDate);
        break;
      case 'less than or equal to':
        if (filters.actualStartDate) searchParams.actualStartDateTo = this.formatDate(filters.actualStartDate);
        break;
    }

    switch (filters.actualEndDateOption) {
      case 'equal to':
        if (filters.actualEndDate) {
          searchParams.actualEndDateFrom = this.formatDate(filters.actualEndDate);
          searchParams.actualEndDateTo = this.formatDate(filters.actualEndDate);
        }
        break;
      case 'not equal to':
        if (filters.actualEndDate) {
          searchParams.actualEndDateFrom = this.formatDate(new Date(new Date(filters.actualEndDate).setDate(new Date(filters.actualEndDate).getDate() + 1)));
          searchParams.actualEndDateTo = this.formatDate(new Date(new Date(filters.actualEndDate).setDate(new Date(filters.actualEndDate).getDate() - 1)));
        }
        break;
      case 'greater than or equal to':
        if (filters.actualEndDate) searchParams.actualEndDateFrom = this.formatDate(filters.actualEndDate);
        break;
      case 'less than or equal to':
        if (filters.actualEndDate) searchParams.actualEndDateTo = this.formatDate(filters.actualEndDate);
        break;
    }

    this.settlementService.searchSettlements(searchParams).subscribe({
      next: (page) => {
        this.settlements = page.content;
        this.totalRecords = page.totalElements;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load settlements',
        });
      },
    });
  }

  onSearch() {
    this.dt.reset();
  }

  onReset() {
    this.filterForm.reset({
      id: null,
      salesRepNames: [],
      statuses: [],
      promoPlanIds: null,
      actualStartDate: null,
      actualEndDate: null,
      customers: null,
      channels: [],
      branchNames: [],
      businessUnitNames: [],
      businessNames: [],
      activityTypeNames: [],
      supplierNames: [],
      note: null,
      value: null,
      vat: null,
      totalAmount: null,
      customerInvoiceNumbers: [],
      actualStartDateOption: 'equal to',
      actualEndDateOption: 'equal to'
    });
    this.dt.reset();
  }

  private createPromoPlanPickerAdapter(): EntityDropdownPickerAdapter<PromoPlanHeader> {
    return {
      entityKey: 'promo-plan',
      loadRecent: (limit?: number) => this.promoPlanService.loadRecentPromoPlans(limit),
      search: (query: string, page: number, size: number) => this.searchPromoPlans(query, page, size),
      getFreshById: (id: number | string) => this.promoPlanService.getPromoPlanHeaderById(Number(id)),
      rememberSelection: (promoPlan: PromoPlanHeader) => this.promoPlanService.rememberPromoPlan(promoPlan),
      getId: (promoPlan: PromoPlanHeader) => promoPlan.promoPlanId ?? 0,
      getLabel: (promoPlan: PromoPlanHeader) => {
        const promoPlanId = promoPlan.promoPlanId ?? '';
        const description = promoPlan.promoDescription ?? '';
        return `Promo Plan# ${promoPlanId}${description ? ` - ${description}` : ''}`.trim();
      },
      getSecondaryLabel: (promoPlan: PromoPlanHeader) =>
        promoPlan.customer
          ? `${promoPlan.customer.customerNumber ?? ''}${promoPlan.customer.customerName ? ` - ${promoPlan.customer.customerName}` : ''}`.trim()
          : '',
      filterBy: 'promoPlanId',
    };
  }

  private searchPromoPlans(query: string, page: number, size: number) {
    const normalized = query.trim().toLowerCase();
    const promoPlanId = Number(normalized);
    if (Number.isNaN(promoPlanId)) {
      return of({
        content: [],
        pageable: {
          pageNumber: page,
          pageSize: size,
          sort: { sorted: false, unsorted: true, empty: true },
          offset: page * size,
          paged: true,
          unpaged: false,
        },
        last: true,
        totalPages: 0,
        totalElements: 0,
        size,
        number: page,
        sort: { sorted: false, unsorted: true, empty: true },
        first: true,
        numberOfElements: 0,
        empty: true,
      });
    }

    return this.promoPlanService.searchPromoPlanHeaders({
      promoPlanIds: [promoPlanId],
      page,
      size,
      sort: 'promoPlanId,desc',
    });
  }

  onLazyLoad(event: any) {
    this.loadSettlements(event);
  }

  onNewSettlement(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['settlement', 'add'] } },
    ]);
  }

  onViewSettlement(settlement: Settlement): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['settlement', 'view', settlement.id] } },
    ]);
  }

  clear(table: Table): void {
    table.clear();
  }

  private formatDate(date: Date | null): string | null {
    if (!date) {
      return null;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getStatusSeverity(status: string): string {
    if (!status) {
      return 'secondary';
    }
    switch (status.toLowerCase()) {
      case 'pending - sales':
      case 'pending - marketing':
        return 'warn';
      case 'pending - budgetary control':
      case 'pending - a&p controller':
      case 'pending - finance ar':
        return 'info';
      case 'completed':
        return 'success';
      default: return 'secondary';
    }
  }

  exportExcel() {
    const formValues = this.filterForm.value;
    const isFilterFormEmpty = Object.keys(formValues).every(key => {
      const value = formValues[key];
      if (key === 'actualStartDateOption' || key === 'actualEndDateOption') {
        return true; // Ignore default dropdown values
      }
      return value === null || value === '' || (Array.isArray(value) && value.length === 0);
    });

    if (isFilterFormEmpty) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Filters Applied',
        detail: 'Please apply at least one filter to export the data.'
      });
      return;
    }

    this.isExporting = true;
    const filters = this.filterForm.value;

    const searchParams: any = {
      settlementId: filters.id,
      salesRepNames: filters.salesRepNames?.map((r: AppUser) => r.fullName),
      statuses: filters.statuses,
      promoPlanIds: filters.promoPlanIds?.promoPlanId ? [filters.promoPlanIds.promoPlanId] : undefined,
      customerNumbers: filters.customers?.customerNumber ? [filters.customers.customerNumber] : undefined,
      channels: filters.channels,
      branchNames: filters.branchNames?.map((b: Branch) => b.name),
      businessUnitNames: filters.businessUnitNames?.map((bu: BusinessUnit) => bu.businessUnitName),
      businessNames: filters.businessNames?.map((b: Business) => b.businessName),
      activityTypeNames: filters.activityTypeNames?.map((at: ActivityType) => at.name),
      supplierNames: filters.supplierNames?.map((s: Supplier) => s.supplierName),
      note: filters.note,
      value: filters.value,
      vat: filters.vat,
      totalAmount: filters.totalAmount,
      customerInvoiceNumbers: filters.customerInvoiceNumbers,
      sort: 'id,desc'
    };

    // Date logic can be reused from loadSettlements if extracted, or duplicated here.
    // For simplicity, it's duplicated here.
    if (filters.actualStartDate) {
      if (filters.actualStartDateOption === 'equal to') {
        searchParams.actualStartDateFrom = this.formatDate(filters.actualStartDate);
        searchParams.actualStartDateTo = this.formatDate(filters.actualStartDate);
      } else if (filters.actualStartDateOption === 'greater than or equal to') {
        searchParams.actualStartDateFrom = this.formatDate(filters.actualStartDate);
      } else if (filters.actualStartDateOption === 'less than or equal to') {
        searchParams.actualStartDateTo = this.formatDate(filters.actualStartDate);
      }
    }
    if (filters.actualEndDate) {
      if (filters.actualEndDateOption === 'equal to') {
        searchParams.actualEndDateFrom = this.formatDate(filters.actualEndDate);
        searchParams.actualEndDateTo = this.formatDate(filters.actualEndDate);
      } else if (filters.actualEndDateOption === 'greater than or equal to') {
        searchParams.actualEndDateFrom = this.formatDate(filters.actualEndDate);
      } else if (filters.actualEndDateOption === 'less than or equal to') {
        searchParams.actualEndDateTo = this.formatDate(filters.actualEndDate);
      }
    }

    Object.keys(searchParams).forEach(key => (searchParams[key] == null || (Array.isArray(searchParams[key]) && searchParams[key].length === 0)) && delete searchParams[key]);

    const pageSize = 1000;
    const firstPageParams = { ...searchParams, page: 0, size: pageSize };

    this.settlementService.searchSettlements(firstPageParams).pipe(
      switchMap((firstPage: Page<Settlement>) => {
        const totalPages = firstPage.totalPages;
        if (totalPages <= 1) {
          return of(firstPage.content);
        }

        const pageRequests$ = [];
        for (let i = 1; i < totalPages; i++) {
          const pageParams = { ...searchParams, page: i, size: pageSize };
          pageRequests$.push(this.settlementService.searchSettlements(pageParams).pipe(map(page => page.content)));
        }

        return forkJoin(pageRequests$).pipe(
          map(otherPagesContents => [firstPage.content, ...otherPagesContents].flat())
        );
      }),
      catchError(err => {
        this.isExporting = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch all data for export.' });
        console.error('Error exporting settlements:', err);
        return of(null);
      })
    ).subscribe((allSettlements: Settlement[] | null) => {
      if (allSettlements) {
        const dataToExport = allSettlements.map(s => ({
          'Settlement ID': s.id,
          'Sales Rep': s.salesRep?.fullName,
          'Creation Date': formatNormalizedDate(s.createdAt),
          'Last Update Date': formatNormalizedDate(s.updatedAt),
          'Status': s.status?.statusName,
          'Customer Name': s.customer?.customerName,
          'Customer #': s.customer?.customerNumber,
          'Location': s.location?.locationNo,
          'Supplier': s.supplierName,
          'AP Voucher': s.apVoucher,
          'Transaction Type': s.transactionType,
          'Proposed Text Reference': s.proposedTextReference,
          'Proposed AR GL Code': s.proposedArGlCodeCombination,
          'Proposed AP GL Code': s.proposedApGlCodeCombination,
          'A&P Value': s.apValue,
          'A&P VAT': s.apVat,
          'A&P Total': s.apTotalAmount,
          'Text Reference': s.apTextReference,
          'AR GL Code': s.apArGlCodeCombination,
          'AP GL Code': s.apApGlCodeCombination,
          'Promo Plan ID': s.promoPlanId,
          'Actual Start Date': formatNormalizedDate(s.startDate),
          'Actual End Date': formatNormalizedDate(s.endDate),
          'Customer Invoice #': s.customerInvoiceNumber,
          'Channel': s.channel,
          'Branch': s.branch?.name,
          'Business Unit': s.businessUnit?.businessUnitName,
          'Claim Brand': s.business?.businessName,
          'Activity Type': s.activityType?.name,
          'Note': s.note,
          'Sales Amount': s.value,
          'VAT': s.vat,
          'Total': s.totalAmount
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Settlements');
        XLSX.writeFile(wb, 'Settlements_Export.xlsx');
      }
      this.isExporting = false;
    });
  }
}
